import { create } from "zustand"

export type SecurityFlow =
	| "idle"
	| "email-change"
	| "password-change"
	| "mfa-setup"
	| "username-to-email"

export const SECURITY_FLOWS = {
	IDLE: "idle" as const,
	EMAIL_CHANGE: "email-change" as const,
	PASSWORD_CHANGE: "password-change" as const,
	MFA_SETUP: "mfa-setup" as const,
	USERNAME_TO_EMAIL: "username-to-email" as const
} as const

export interface SecurityFlowStep {
	step: string
	data?: Record<string, unknown>
}

export type MfaMethod = "EMAIL" | "SMS"

export interface SecurityUser {
	email: string
	emailVerified: boolean
	username?: string
	phone?: string | null
	phoneVerified?: boolean
	primaryMFA: MfaMethod | null
	secondaryMFA: MfaMethod | null
	emailIsUsername: boolean
	lastPasswordUpdate?: string
}

interface SecuritySectionStore {
	// Current flow state
	currentFlow: SecurityFlow
	currentStep: SecurityFlowStep | null

	// User data
	user: SecurityUser | null

	// UI state
	error: string | null

	// Verification token used for OTP flows and secured updates
	token: string | null

	// Flow navigation
	setCurrentFlow: (flow: SecurityFlow) => void
	setCurrentStep: (step: SecurityFlowStep | null) => void
	goToIdle: () => void

	// User data management
	setUser: (user: SecurityUser) => void
	updateUser: (updates: Partial<SecurityUser>) => void

	// UI state management
	setError: (error: string | null) => void
	clearError: () => void

	// Verification token management
	setToken: (token: string | null) => void
	clearToken: () => void

	// Reset store
	reset: () => void
}

const initialState = {
	currentFlow: SECURITY_FLOWS.IDLE as SecurityFlow,
	currentStep: null,
	user: null,
	isLoading: false,
	error: null,
	token: null
}

export const useSecuritySectionStore = create<SecuritySectionStore>((set) => ({
	...initialState,

	// Flow navigation
	setCurrentFlow: (flow) =>
		set({ currentFlow: flow, currentStep: null, error: null }),
	setCurrentStep: (step) => set({ currentStep: step }),
	goToIdle: () =>
		set({ currentFlow: SECURITY_FLOWS.IDLE, currentStep: null, error: null }),

	// User data management
	setUser: (user) => set({ user }),
	updateUser: (updates) =>
		set((state) => ({
			user: state.user ? { ...state.user, ...updates } : null
		})),

	// UI state management
	setError: (error) => set({ error }),
	clearError: () => set({ error: null }),

	// Verification token management
	setToken: (token) => set({ token: token }),
	clearToken: () => set({ token: null }),

	// Reset store
	reset: () => set(initialState)
}))
